import { BorderColorOutlined, Delete, People } from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonGroup, Card, Grid, TextField, Typography
} from "@mui/material";
import MaterialReactTable from "material-react-table";
import { FC, UIEvent, useEffect, useMemo, useRef, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { API_SLUGS, SQL_INJECTION_PATTERNS } from "../../../constants";
import useFetch from "../../../hooks/http/useFetch";
import usePermissionChecker from "../../../hooks/process/usePermissionChecker";
import { useTableInfinity } from "../../../hooks/process/useTableInfinity";
import { axiosInstance } from "../../../services/axios";
import { useAlertState } from "../../../store/alertState";
import { PermissionTargets, PermissionTypes } from "../../../store/userState";
import ModalWrapper from "../../common/modal/wrapper";
import SearchComponent from "../../common/search";
import TruncatedScrollingText from "../../common/truncatedScrollingText";
import AddGroupForm from "./AddGroupForm";
import MembersTable from "./MembersTable";

const limit = 20;
const disableInfiniteScroll = false;
const GroupsTable: FC<RouteComponentProps> = ({ history }) => {

  const setMessage = useAlertState(state =>
    //@ts-ignore
    state.setMessage)
  const [searchQuery, setSearchQuery] = useState("");
  // const { data: groupPermissions } = useFetch(`/permissions?resourceType=group&permissionType=deny`);
  const groupsUrl = API_SLUGS.GET_GROUPS + `?limit={limit}&offset={offset}`;

  const groupsUrlToUse = useMemo(() => groupsUrl + `&search=${searchQuery}`, [groupsUrl, searchQuery])
  // const createGroupPermission = usePermissionChecker(
  //   PermissionTargets.group,
  //   PermissionTypes.create,
  //   groupPermissions?.permissions
  // );
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { rows, fetchMoreOnBottomReached, isFetching, resetAndFetch } = useTableInfinity(
    "groups",
    groupsUrlToUse,
    tableContainerRef,
    limit
  );


  useEffect(() => {
    if (!disableInfiniteScroll) {
      fetchMoreOnBottomReached(tableContainerRef.current);
    }
  }, [fetchMoreOnBottomReached])

  const onSubmit = (data: string) => {

    const trimmedData = data.trim();
    for (const pattern of SQL_INJECTION_PATTERNS) {
      if (pattern.test(trimmedData)) {
        setMessage({ title: "Invalid search query. Please avoid special characters and SQL keywords.", type: "error" })
        return;
      }
    }
    setSearchQuery(trimmedData)
  }

  return (
    <Card sx={{ textAlign: "left" }}>
      <SearchComponent
        onSubmit={onSubmit}
      />
      <MaterialReactTable muiTablePaperProps={{
        elevation: 0
      }} enableGlobalFilter={false}
        columns={[
          {
            accessorKey: "name",
            header: "Name",
            Cell: (params: any) => <TruncatedScrollingText name={params?.row?.original?.name} maxLength={15} />,
            size: 130,
          },

          {
            accessorKey: "actions",

            header: "Actions",
            Cell: (params: any) => (
              <Actions params={params} fetchData={resetAndFetch} history={history} />
            ),
          },
        ]}
        data={rows}
        initialState={{
          density: "compact",

        }}
        enableColumnActions={false}
        enableSorting={false}
        enableStickyHeader



        enableTopToolbar={false}
        enablePagination={false}
        enableDensityToggle={false}
        enableColumnFilters={false}
        // enableRowVirtualization
        state={{
          showProgressBars: isFetching,
        }}
        muiTableContainerProps={{
          ref: tableContainerRef, //get access to the table container element
          sx: { maxHeight: "calc(100vh - 300px)" }, //give the table a max height
          onScroll: (
            event: UIEvent<HTMLDivElement> //add an event listener to the table container element
          ) => {
            if (!disableInfiniteScroll) {
              fetchMoreOnBottomReached(event.target as HTMLDivElement)
            }

          }
        }}
      />
    </Card>
  );
};

const Actions: FC<any> = ({ params, history, fetchData }) => {
  const { data: memberPermissions } = useFetch(`/permissions?resourceType=group&permissionType=deny&resourceId=${params?.row?.original?.id}`);
  const deleteGroupPermission = usePermissionChecker(
    PermissionTargets.group,
    PermissionTypes.delete,
    memberPermissions?.permissions
  );
  const [allowDelete, setAllowDelete] = useState(false);

  const handleDelete = async (id: string) => {
    await axiosInstance.delete(`/group/${id}`);
    fetchData();
  };

  return <Box sx={{ display: "flex", justifyContent: "start" }}>
    <ButtonGroup>
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={() =>
          history.push(
            `/projects?groupId=${params?.row?.original?.id}`
          )
        }
      >

        Projects
      </Button>
      <ModalWrapper
        maxWidth="lg"
        title={params?.row?.original?.name}
        trigger={
          <Button>
            <People />
          </Button>
        }
        child={
          <Box>


            <MembersTable groupId={params?.row?.original?.id} />
          </Box>
        }
      />

      <ModalWrapper
        maxWidth="sm"
        title="Edit"
        trigger={
          <Button>
            <BorderColorOutlined />
          </Button>
        }
        child={
          <AddGroupForm
            updateOnly
            updateUUID={params?.row?.original?.id}
            defaultFormValues={{ group: params?.row?.original?.name }}
          />
        }
      />
      <ModalWrapper
        maxWidth="sm"
        title="Delete Group"
        trigger={
          <Button color="error" disabled={!deleteGroupPermission.hasPermission}>
            <Delete />
          </Button>
        }
        child={
          <Grid container spacing={3}>
            <Grid item xs={12}>
            <Typography
                color="error"
                sx={{ mt: 1, mb: 3, textAlign: "left" }}
                variant="h6"
              >
                This action will delete the Group, along with all associated Projects, Assets, and the Run Details for each asset.
              </Typography>
              <Typography
                color="textPrimary"
                sx={{ mb: 1, textAlign: "left" }}
                variant="h6"
              >
                Enter group name to
                delete the group
              </Typography>

              <TextField
                autoComplete="off"
                sx={{ maxWidth: "50%", textAlign: "left" }}
                size="medium"
                fullWidth
                name="Api"

                placeholder={params?.row?.original?.name}
                onChange={(e: any) => {
                  setAllowDelete(e.target.value === params?.row?.original?.name);
                }}
                required
                variant="outlined"
              />

            </Grid>
            <Grid item xs={12}>
              <Button
                color="error"
                size="medium"
                variant="contained"
                disabled={!allowDelete}
                onClick={() => handleDelete(params?.row?.original?.id)}
                sx={{ mr: 1 }}
              >
                Delete
              </Button>
            </Grid>
          </Grid>
        }
      />
    </ButtonGroup>
  </Box>
}

export default withRouter(GroupsTable);
